import React, { useState, useRef, useCallback } from 'react'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import { useChatWithSteps } from '../hooks/useChatWithSteps'
import { useAsyncTaskTracker } from '../hooks/useAsyncTaskTracker'
import { PARTICIPANT_COLORS, hashString, MYAGENT_PARTICIPANT_ID, MYAGENT_COLOR } from './participantColors.js'
import { BotIcon, BackIcon, PeopleIcon } from './icons.jsx'
import './ChatInterface.css'

/**
 * ChatInterface — pure chat view (messages + input + bot drawer)
 *
 * All navigation and conversation state is managed by AppLayout.
 * This component focuses on displaying and sending messages.
 */
export const ChatInterface = ({
  api,
  onAuthError,
  peerService,
  roleCall,
  activeConversationId,
  conversationTarget,
  participants,
  contacts,
  emptyMessage = 'How can I help you today?',
  placeholder = 'Message MyAgent...',
  chatTitle = 'MyAgent',
  onBack,
  onParticipantsChanged,
  onAddParticipant,
  onRemoveParticipant,
  onRefreshContacts,
  onConfigureBot,
  botConfig = null,
}) => {
  // Async task tracker ref
  const trackRef = useRef(null)
  const onAsyncTask = useCallback(({ taskId, roleId, displayName, conversationId }) => {
    trackRef.current?.(taskId, { roleId, displayName, conversationId })
  }, [])

  const { messages, isThinking, isLoadingHistory, sendMessage, appendMessage } = useChatWithSteps({
    api,
    onAuthError,
    peerService,
    onAsyncTask,
    onParticipantsChanged,
    conversationId: activeConversationId,
    conversationTarget,
    botConfig,
  })

  // Track active conversation via ref for async task handler
  const activeConvIdRef = useRef(activeConversationId)
  activeConvIdRef.current = activeConversationId

  const handleTrackerMessage = useCallback((msg) => {
    const msgConvId = msg.metadata?.conversationId || null
    if (msgConvId && api) {
      api.saveConversationMessage(msgConvId, [msg]).catch(e => console.warn('[ChatInterface] Failed to persist tracker message:', e.message))
    } else if (api) {
      api.saveStepToSession({ participantMessages: [msg] }).catch(e => console.warn('[ChatInterface] Failed to persist tracker message:', e.message))
    }
    if (msgConvId === activeConvIdRef.current) {
      appendMessage(msg)
    }
  }, [api, appendMessage])

  const { track } = useAsyncTaskTracker({ forwardCallFn: roleCall, onMessage: handleTrackerMessage, ready: !isLoadingHistory })
  trackRef.current = track

  // Bot drawer (local UI state)
  const [showBotDrawer, setShowBotDrawer] = useState(false)

  const openBotDrawer = useCallback(() => {
    setShowBotDrawer(true)
    onRefreshContacts?.()
  }, [onRefreshContacts])

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-header-left">
            {onBack && (
              <button className="chat-back-button" onClick={onBack} title="Back">
                <BackIcon />
              </button>
            )}
            <h1 className="chat-title">{chatTitle}</h1>
          </div>
          <div className="chat-header-right">
            <button className="bots-button" onClick={openBotDrawer} title="Participants">
              <PeopleIcon size={16} />
              {!activeConversationId && participants.length > 1 && <span className="bots-count">{participants.length}</span>}
            </button>
          </div>
        </div>
      </div>

      <MessageList
        messages={messages}
        isThinking={isThinking}
        isLoadingHistory={isLoadingHistory}
        emptyMessage={emptyMessage}
        participants={participants}
        contacts={contacts}
      />

      <ChatInput
        onSend={sendMessage}
        disabled={isThinking}
        placeholder={placeholder}
        participants={conversationTarget ? [conversationTarget] : participants}
      />

      {showBotDrawer && (
        <>
          <div className="bot-drawer-overlay" onClick={() => setShowBotDrawer(false)} />
          <div className="bot-drawer">
            <div className="bot-drawer-header">
              <h2 className="bot-drawer-title">Participants</h2>
              <button className="bot-drawer-close" onClick={() => setShowBotDrawer(false)}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bot-drawer-content">
              {activeConversationId ? (
                // Direct conversation: show the target bot only
                conversationTarget && (() => {
                  const color = PARTICIPANT_COLORS[hashString(conversationTarget.roleId) % PARTICIPANT_COLORS.length]
                  const online = conversationTarget.online ?? false
                  return (
                    <div className="bot-section">
                      <div className="bot-section-title">In Conversation</div>
                      <div className="bot-item">
                        <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                          <BotIcon color={color} />
                          <span className={`bot-status-dot ${online ? 'online' : 'offline'}`} />
                        </div>
                        <div className="bot-info">
                          <div className="bot-name">{conversationTarget.displayName}</div>
                        </div>
                        <span className="bot-badge-host">1:1</span>
                      </div>
                    </div>
                  )
                })()
              ) : (
                // Main chat: In Chat + Available sections
                <>
                  {participants.length > 0 && (
                    <div className="bot-section">
                      <div className="bot-section-title">In Chat</div>
                      {participants.map(p => {
                        const isMyAgent = p.roleId === MYAGENT_PARTICIPANT_ID
                        const peer = contacts.find(c => c.roleId === p.roleId)
                        const online = isMyAgent || (peer?.online ?? false)
                        const color = isMyAgent ? MYAGENT_COLOR : PARTICIPANT_COLORS[hashString(p.roleId) % PARTICIPANT_COLORS.length]
                        return (
                          <div key={p.roleId} className="bot-item">
                            <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                              <BotIcon color={color} />
                              <span className={`bot-status-dot ${online ? 'online' : 'offline'}`} />
                            </div>
                            <div className="bot-info">
                              <div className="bot-name">{p.displayName}{p.typeName && p.displayName !== p.typeName && <span className="bot-type"> ({p.typeName})</span>}</div>
                              {p.description && <div className="bot-description">{p.description}</div>}
                            </div>
                            {isMyAgent
                              ? <span className="bot-badge-host">Host</span>
                              : <>
                                  {onConfigureBot && <button className="bot-action bot-action-config" onClick={() => onConfigureBot(p)} title="Configure">&#9881;</button>}
                                  <button className="bot-action bot-action-remove" onClick={() => onRemoveParticipant(p.roleId)}>Remove</button>
                                </>
                            }
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {(() => {
                    const available = contacts.filter(c => !participants.some(p => p.roleId === c.roleId))
                    if (available.length === 0 && participants.length === 0) {
                      return (
                        <div className="bot-empty">
                          <BotIcon size={40} color="var(--text-dim)" />
                          <p className="bot-empty-text">No bots available</p>
                          <p className="bot-empty-hint">No bots registered yet</p>
                        </div>
                      )
                    }
                    return available.length > 0 ? (
                      <div className="bot-section">
                        <div className="bot-section-title">Available</div>
                        {available.map(c => {
                          const online = c.online
                          const color = PARTICIPANT_COLORS[hashString(c.roleId) % PARTICIPANT_COLORS.length]
                          return (
                            <div key={c.roleId} className={`bot-item ${online ? 'bot-item-clickable' : 'bot-item-disabled'}`} onClick={online ? () => onAddParticipant(c) : undefined}>
                              <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                                <BotIcon color={online ? color : 'var(--text-tertiary)'} />
                                <span className={`bot-status-dot ${online ? 'online' : 'offline'}`} />
                              </div>
                              <div className="bot-info">
                                <div className="bot-name">{c.displayName}{c.typeName && c.displayName !== c.typeName && <span className="bot-type"> ({c.typeName})</span>}</div>
                                {c.description && <div className="bot-description">{c.description}</div>}
                              </div>
                              {online && <span className="bot-action bot-action-add">Add</span>}
                            </div>
                          )
                        })}
                      </div>
                    ) : null
                  })()}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
